/**
 * MovilMenu Component
 *
 * Description:
 * Mobile navigation menu component that renders a collapsible drawer with navigation links. Displays different menu options based on user authentication status and integrates with Redux for user and academy data management.
 *
 * Features:
 * - Responsive mobile drawer navigation with Material-UI components.
 * - Dynamic menu rendering based on user authentication status.
 * - Integration with academy-specific menu items when available.
 * - Logout functionality with Redux action dispatch.
 * - Translation support for multi-language menu items.
 * - URL-based conditional rendering for context-appropriate menus.
 * - Toggle functionality for opening/closing the drawer.
 *
 * Props:
 * (No props required. All state is managed internally and from Redux.)
 *
 * Hooks & State:
 * - useState:
 *   - state: Controls the open/closed state of the mobile drawer.
 * - useDispatch: Dispatches Redux actions for user logout.
 * - useSelector: Accesses Redux state for academy info and user data.
 * - useTranslation: Provides translation for menu text items.
 *
 * Render Flow:
 * 1. Renders hamburger menu icon button to trigger drawer.
 * 2. Conditionally renders menu items based on user authentication:
 *    - If logged in: Shows login menu, academy menu (if available), and logout option.
 *    - If not logged in: Shows no-user menu items.
 * 3. Handles drawer toggle functionality for opening/closing.
 * 4. Integrates with routing for navigation and external links.
 *
 * Good Practices / Warnings:
 * - Component relies on global window object for URL parsing.
 * - Consider using React Router hooks for better routing integration.
 * - Ensure menu data constants are properly maintained for menu updates.
 * - Redux state should be properly managed for user authentication.
 */

import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import Drawer from '@mui/material/Drawer';

import AcademyMenuMovil from 'components/UI/AcademyMenuMovil/AcademyMenuMovil';

import { logOut } from 'store/user-actions';
import { getLoginMenuData } from 'constants/LoginMenu';
import { getNoUserMenuData } from 'constants/NoUserMenu';

import classes from './MovilMenu.module.css';

const url = window.location.pathname.split('/');

const MovilMenu = () => {
  const { t } = useTranslation(['header']);
  const dispatch = useDispatch();
  const dataAcademy = useSelector(state => state.academyInfo);
  const userInfo = useSelector(state => state.userInfo);

  // State for controlling drawer open/close
  const [state, setState] = useState(false);

  /**
   * Toggles the drawer open/close state
   */
  const toggleDrawer = () => () => {
    setState(!state);
  };

  /**
   * Handles user logout by dispatching logout action
   */
  const logoutUser = () => {
    dispatch(logOut());
  };

  return (
    <div className={classes.movilMenuContainer}>
      <IconButton
        onClick={toggleDrawer(state, true)}
        edge="start"
        color="inherit"
        aria-label="menu"
        sx={{ mr: 2 }}
      >
        <MenuIcon className={classes.menuIcon} />
      </IconButton>
      <Drawer
        anchor="left"
        open={state} // Drawer state control prop
        onClose={toggleDrawer(state, false)} // Close handler prop
      >
        {userInfo.data.usuario ? (
          <ul className={classes.menuContainer}>
            {getLoginMenuData().map(item => (
              <li className={classes.itemMenu} key={item.link}>
                {item.openInDiferentPage ? (
                  <Button
                    className={classes.itemLink}
                    component="a"
                    href={item.link}
                  >
                    {t(item.text)}
                  </Button>
                ) : (
                  <Link
                    className={classes.itemLink}
                    onClick={toggleDrawer(state, false)} // Close drawer on navigation
                    to={item.link}
                  >
                    {t(item.text)}
                  </Link>
                )}
              </li>
            ))}
            <Divider />
            {dataAcademy.menu.length > 0 && url[2] !== 'course' && (
              <AcademyMenuMovil
                menu={dataAcademy.menu} // Academy menu items prop
              />
            )}
            <Divider />
            <li className={classes.itemMenu}>
              <Button
                className={classes.itemLink}
                onClick={logoutUser} // Logout handler
              >
                Cerrar sesión
              </Button>
            </li>
          </ul>
        ) : (
          <ul className={classes.menuContainer}>
            {getNoUserMenuData().map(item => (
              <li className={classes.itemMenu} key={item.link}>
                {item.openInDiferentPage ? (
                  <Button
                    className={classes.itemLink}
                    component="a"
                    href={item.link}
                  >
                    {t(item.text)}
                  </Button>
                ) : (
                  <Link
                    className={classes.itemLink}
                    onClick={toggleDrawer(state, false)} // Close drawer on navigation
                    to={item.link}
                  >
                    {t(item.text)}
                  </Link>
                )}
              </li>
            ))}
          </ul>
        )}
      </Drawer>
    </div>
  );
};

export default MovilMenu;
